import { useEffect, useRef } from 'react';
import { Message } from '../../types/message.types';
import { CodeTab } from '../../types/project.types';
import { MessageBubble } from './MessageBubble';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  onExamplePromptSelect?: (prompt: string) => void;
  onOpenFile?: (file: CodeTab) => void;
}

const EXAMPLE_PROMPTS = [
  'Build a task manager with user accounts and due dates',
  'Create a feedback form with a dashboard to view responses',
  'Build an invoice generator with PDF export',
  'Make a simple CRM to track contacts and notes',
  'Build a booking system with a calendar view',
];

export function MessageList({ messages, isLoading, onExamplePromptSelect, onOpenFile }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-8">
        <div className="mx-auto max-w-xl rounded-2xl border border-dashed border-neutral-300 bg-white p-6 text-left shadow-sm">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-neutral-500">
            Start here
          </p>
          <p className="mb-6 text-base text-neutral-700">
            Describe the app you want to build. Be as specific as you like - include
            features, user roles, and any integrations you need.
          </p>
          <div className="space-y-3">
            {EXAMPLE_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => onExamplePromptSelect?.(prompt)}
                className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-left text-sm text-neutral-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-neutral-900"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} onOpenFile={onOpenFile} />
      ))}
      {isLoading && (
        <div className="flex justify-start mb-4">
          <div className="bg-gray-100 rounded-lg px-4 py-3 border border-gray-200">
            <LoadingSpinner size="sm" />
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
